import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'
import { Search, X, Loader2, Hash, Users as UsersIcon, MessageSquare } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { searchTeamMessages } from '@/lib/searchTeamMessages'
import { renderWithMentions } from '@/lib/mentions'

/**
 * Cross-channel team-message search (search CP2 — UI only).
 *
 * Wires the proven searchTeamMessages helper (RLS-relying, leak-proof — a
 * caller only ever matches messages in channels they participate in) to a
 * search overlay. Typing debounces (250ms) into the helper; each result opens
 * its conversation via onSelect (the caller navigates + closes).
 *
 * Conversation display names + routes are resolved from team_conversations +
 * participants, mirroring ChannelSidebar's naming (channel -> #name, DM -> the
 * other participant, group -> joined participant names). Scroll-to-highlight of
 * the specific message is a deliberate fast-follow; v1 opens the channel.
 */

const DEBOUNCE_MS = 250

interface ConvMeta {
  kind: 'channel' | 'dm' | 'group'
  /** Display label, e.g. "#general", a teammate's name, or joined group names. */
  label: string
  /** Route to navigate to when a result in this conversation is clicked. */
  route: string
}

function formatGroupName(otherNames: string[]): string {
  if (otherNames.length === 0) return 'Group chat'
  if (otherNames.length <= 3) return otherNames.join(', ')
  return `${otherNames.slice(0, 3).join(', ')} +${otherNames.length - 3} more`
}

export default function SearchMessagesModal({
  onClose,
  onSelect,
}: {
  onClose: () => void
  onSelect: (route: string) => void
}) {
  const { profile } = useAuth()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')

  // Debounce the raw input into the value we actually search on.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [query])

  const hasQuery = debounced.length > 0

  const {
    data: results = [],
    isLoading: searching,
    isError: searchError,
  } = useQuery({
    queryKey: ['team-message-search', debounced],
    enabled: hasQuery,
    queryFn: () => searchTeamMessages(debounced),
  })

  // Distinct conversation ids across the results, sorted for a stable query key.
  const convIds = useMemo(
    () => [...new Set(results.map(r => r.team_conversation_id))].sort(),
    [results],
  )

  // Resolve each result's conversation -> display label + route. RLS-scoped:
  // these lookups only return conversations the caller participates in (which
  // is exactly the set the results can come from), so nothing leaks here either.
  const { data: convMeta } = useQuery<Record<string, ConvMeta>>({
    queryKey: ['team-search-conv-meta', convIds],
    enabled: convIds.length > 0,
    queryFn: async () => {
      const { data: convs, error: cErr } = await supabase
        .from('team_conversations')
        .select('id, type, name')
        .in('id', convIds)
      if (cErr) throw cErr

      const dmGroupIds = (convs ?? [])
        .filter(c => c.type === 'dm' || c.type === 'group')
        .map(c => c.id)

      // Other-participant names for DM/group labels (excluding self).
      const namesByConv: Record<string, string[]> = {}
      if (dmGroupIds.length > 0) {
        const { data: parts, error: pErr } = await supabase
          .from('team_conversation_participants')
          .select('team_conversation_id, user_id, profiles!team_conversation_participants_user_fkey(id, full_name)')
          .in('team_conversation_id', dmGroupIds)
        if (pErr) throw pErr
        for (const p of (parts ?? []) as any[]) {
          if (p.user_id === profile?.id) continue
          ;(namesByConv[p.team_conversation_id] ??= []).push(p.profiles?.full_name || 'Unknown user')
        }
      }

      const map: Record<string, ConvMeta> = {}
      for (const c of (convs ?? []) as any[]) {
        if (c.type === 'channel') {
          map[c.id] = { kind: 'channel', label: `#${c.name}`, route: `/app/team-chat/${c.name}` }
        } else if (c.type === 'dm') {
          map[c.id] = { kind: 'dm', label: namesByConv[c.id]?.[0] || 'Direct message', route: `/app/team-chat/dm/${c.id}` }
        } else {
          map[c.id] = { kind: 'group', label: formatGroupName(namesByConv[c.id] ?? []), route: `/app/team-chat/group/${c.id}` }
        }
      }
      return map
    },
  })

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 100, padding: 24, paddingTop: '10vh' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Search input row */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Search style={{ width: 18, height: 18, color: 'var(--ink-tertiary)', flexShrink: 0 }} />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') onClose() }}
            placeholder="Search messages…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 15, color: 'var(--ink-primary)' }}
          />
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 4, display: 'flex', flexShrink: 0 }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Results / states */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {!hasQuery && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>
              Type to search your team messages.
            </div>
          )}

          {hasQuery && searching && (
            <div style={{ padding: '40px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-tertiary)' }}>
              <Loader2 style={{ width: 16, height: 16, marginRight: 8 }} className="animate-spin" />
              <span style={{ fontSize: 13 }}>Searching…</span>
            </div>
          )}

          {hasQuery && !searching && searchError && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-secondary)', fontSize: 13 }}>
              Something went wrong searching messages. Please try again.
            </div>
          )}

          {hasQuery && !searching && !searchError && results.length === 0 && (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--ink-tertiary)', fontSize: 13 }}>
              No messages found for “{debounced}”.
            </div>
          )}

          {hasQuery && !searching && !searchError && results.length > 0 && (
            <div style={{ padding: '8px 0' }}>
              {results.map(r => {
                const meta = convMeta?.[r.team_conversation_id]
                const ConvIcon = meta?.kind === 'dm' ? MessageSquare : meta?.kind === 'group' ? UsersIcon : Hash
                return (
                  <button
                    key={r.id}
                    onClick={() => { if (meta) onSelect(meta.route) }}
                    disabled={!meta}
                    style={{
                      width: '100%', textAlign: 'left', background: 'none', border: 'none',
                      cursor: meta ? 'pointer' : 'default', padding: '10px 20px',
                      display: 'flex', flexDirection: 'column', gap: 4,
                    }}
                    onMouseEnter={e => { (e.currentTarget.style.background = 'var(--hover-bg)') }}
                    onMouseLeave={e => { (e.currentTarget.style.background = 'none') }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--ink-tertiary)', minWidth: 0 }}>
                      <span style={{ fontWeight: 700, color: 'var(--ink-secondary)' }}>{r.sender_name || 'Unknown'}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <ConvIcon style={{ width: 11, height: 11, flexShrink: 0 }} />
                        {meta?.label ?? '…'}
                      </span>
                      <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</span>
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {renderWithMentions(r.content)}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}
